import { FC, useMemo } from "react";
import {
  Box,
  createStyles,
  makeStyles,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import _, { concat, groupBy, map, sumBy, takeRight } from "lodash";
import { AxisDomain } from "recharts";

import {
  CustomizedXAxisTick,
  EssentialImage,
  GridTable,
  RecentEarnings,
} from "components";
import { ChartType } from "components/RecentEarnings";
import { setYAxisLowerDomain, setYAxisUpperDomain } from "lib/chart";
import { formatAmount } from "lib/format";
import { getEarningAmount } from "lib/helpers";
import { ListingRoyaltyPayor } from "services/listings/types";
import { EarningByRegion } from "services/valuation/types";
import { parseChartDataIntoTuples } from "services/valuation/valuationPostProcessor";
import theme from "theme";

const useStyles = makeStyles(() =>
  createStyles({
    title: {
      fontSize: "18px",
      fontWeight: theme.typography.fontWeightBold,
    },
    subtitle: {
      fontSize: "14px",
    },
    recentEarningsNotice: {
      "& p": {
        fontSize: "0.875rem",
        [theme.breakpoints.down("sm")]: {
          padding: theme.spacing(1),
        },
      },
      "& a": {
        color: theme.custom.palette.blue.main,
        fontSize: "0.875rem",
      },
      [theme.breakpoints.up("xs")]: {
        textAlign: "left",
      },
    },
    recentEarningsTableContainer: {
      [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(1),
      },
    },
  }),
);

const maybeAddAsterisk = (
  year: string,
  latestYear: string,
  isLatestYearComplete: boolean,
) => {
  return year === latestYear && !isLatestYearComplete ? `${year}*` : year;
};

const multiPayorNotice =
  "Note: The earnings reported above reflect royalties as they have been reported from the multiple payors included in this listing. If one of the payors has not yet paid for a reported period it will not be reflected above. Please see “Other Information” for details.";
const bmiEarningsNotice =
  "Note: BMI earnings are reported for the period in which they were earned, not for the period they are paid.";
const bmiRMLCNotice =
  "Note: BMI’s Q2-2020 distribution includes non-recurring settlement payments from the Radio Music Licensing Committee (RMLC). The settlement represents a rate increase for radio performances in years 2017-2019. You can view the amount of the settlement in the Raw Data file under the “raw_music_user” column with the label: “RMLC2017 STLMNT”, “RMLC2018 STLMNT” and “RMLC2019 STLMNT.”";

interface EarningsByPeriodProps {
  earningsByPeriod: [string, EarningByRegion | number][];
  distributionFrequency: number;
  royaltyPayors: ListingRoyaltyPayor[];
  mismatchNotice: string | null;
  isListingOpenForOffers: boolean;
}

const EarningsByPeriod: FC<EarningsByPeriodProps> = ({
  earningsByPeriod,
  distributionFrequency,
  royaltyPayors,
  mismatchNotice,
  isListingOpenForOffers,
}) => {
  const classes = useStyles();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const hasBmi = royaltyPayors.some(payor => payor.name === "bmi");

  const hasNotice = mismatchNotice || hasBmi;

  const {
    chartData,
    xAxisTick,
    yAxisDomain,
    shouldShowUnreportedRegion,
    earningsTableData,
    chartType,
    latestYear,
    isLatestYearComplete,
  } = useMemo(() => {
    const parsedChart = parseChartDataIntoTuples(earningsByPeriod);

    const chartData = takeRight(parsedChart, distributionFrequency * 5);
    const chartType: ChartType = chartData.length
      ? typeof chartData[0].amount === "number"
        ? "bar-chart"
        : "stacked-bar-chart"
      : "stacked-bar-chart";
    const earningsAmountList = chartData.map(data => data.amount);
    const xAxisTick = <CustomizedXAxisTick />;
    const yAxisDomain: [AxisDomain, AxisDomain] = [
      setYAxisLowerDomain(earningsAmountList),
      `dataMax + ${setYAxisUpperDomain(earningsAmountList)}`,
    ];

    const shouldShowUnreportedRegion =
      earningsByPeriod.reduce((previousValue, currentElement) => {
        if (currentElement.length) {
          return (
            previousValue + (currentElement[1] as EarningByRegion).unreported
          );
        }
      }, 0) != 0;

    const chartDataGroupedByYear = groupBy(parsedChart, v => v.date.year());
    const latestYear = Object.keys(chartDataGroupedByYear).sort().pop();
    const isLatestYearComplete =
      chartDataGroupedByYear[latestYear].length === distributionFrequency &&
      !mismatchNotice;

    // used lodash's chaining feature (_(parsedChart)) to combine the groupBy, map, and takeRight operations.
    const earningsTable = _(chartDataGroupedByYear)
      .map((v, k) => ({
        year: k,
        amount: formatAmount(
          sumBy(v, element => getEarningAmount(element.amount)),
        ),
      }))
      .takeRight(isSmDown ? 3 : 5)
      .value();

    const earningsTableData = {
      // create the arrays for title and data. If mobile, remove the first element of the array
      title: isSmDown
        ? map(earningsTable, v =>
            maybeAddAsterisk(v.year, latestYear, isLatestYearComplete),
          )
        : concat(
            ["Year"],
            map(earningsTable, v =>
              maybeAddAsterisk(v.year, latestYear, isLatestYearComplete),
            ),
          ),

      data: isSmDown
        ? [map(earningsTable, v => v.amount)]
        : [
            concat(
              ["Earnings"],
              map(earningsTable, v => v.amount),
            ),
          ],
    };

    return {
      chartData,
      xAxisTick,
      yAxisDomain,
      shouldShowUnreportedRegion,
      earningsTableData,
      chartType,
      latestYear,
      isLatestYearComplete,
    };
  }, [earningsByPeriod, distributionFrequency, isSmDown, mismatchNotice]);

  return (
    <>
      <Box mb={2}>
        <Typography className={classes.title}>Earnings By Period</Typography>
      </Box>
      <RecentEarnings
        data={chartData}
        chartType={chartType}
        padding={0}
        height={isSmDown ? 350 : 500}
        marginHorizontal={8}
        marginVertical={10}
        fillColor={theme.custom.palette.purple.main}
        shouldHideXLabel={false}
        xLabelPosition="insideBottom"
        secondXAxisCustomTicks={xAxisTick}
        customYAxisDomain={yAxisDomain}
        customYAxisScale="linear"
        distributionFrequency={distributionFrequency}
        showUnreportedRegion={shouldShowUnreportedRegion}
      />
      {royaltyPayors.length > 1 && (
        <Box className={classes.recentEarningsNotice}>
          <p>{multiPayorNotice}</p>
        </Box>
      )}
      {hasNotice && (
        <Box className={classes.recentEarningsNotice}>
          {mismatchNotice && <EssentialImage htmlString={mismatchNotice} />}
          {hasBmi && (
            <Box>
              <p>{bmiEarningsNotice}</p>
              <p>{bmiRMLCNotice}</p>
            </Box>
          )}
        </Box>
      )}
      <GridTable
        className={classes.recentEarningsTableContainer}
        titles={earningsTableData.title}
        data={earningsTableData.data}
      />
      {!isLatestYearComplete && (
        <Box
          mt={isSmDown ? 0 : 2}
          mb={-2}
          className={classes.recentEarningsNotice}
        >
          <Typography>{`* ${latestYear} earnings have not been paid for the entire year.${
            isListingOpenForOffers
              ? " The winning buyer will receive the remaining earnings for the year."
              : ""
          }`}</Typography>
        </Box>
      )}
    </>
  );
};

export default EarningsByPeriod;
